import { useParams } from 'react-router-dom';
import { Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Estate_FieldsFragment, useTaskCredit_GetCurrentTaskCreditQuery } from 'queries';

import { Button } from 'common/button';
import Spinner from 'common/spinner';
import PaymentForm from 'pages/common/estate/payment-form';
import PricingRow from 'pages/common/estate/pricing-row';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

type Props = {
  setShowModal: (showModal: boolean) => void;
  estate: Estate_FieldsFragment;
};

const useStyles = makeStyles((theme) => ({
  modalContentWrapper: {
    marginTop: '20px',
    marginBottom: '20px',
    backgroundColor: 'rgba(240, 243, 246, 0.7)',
    padding: '25px',
    paddingTop: '20px',
    '& input': {
      background: 'white',
    },
  },
  cancelButton: {
    marginRight: theme.spacing(2),
  },
  modalButtonWrapper: {
    marginBottom: '20px',
  },
  customizeHeader: {
    textAlign: 'center',
    backgroundColor: '#DAE4ED',
    padding: '5px',
    marginTop: '20px',
    marginBottom: '20px',
  },
}));

export default function EstateTrusuiteSearchPaymentForm({ setShowModal, estate }: Props) {
  const classes = useStyles();
  const { portalId } = useParams<Record<string, string>>();

  const { data: currentTaskCreditData, loading: currentTaskCreditLoading } =
    useTaskCredit_GetCurrentTaskCreditQuery({
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      variables: {
        portalId,
      },
      onError: (e) => {
        showAlert({
          message: 'Error getting current task credit: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  if (!currentTaskCreditData || currentTaskCreditLoading) {
    return <Spinner />;
  }

  const handlePaymentComplete = () => {
    setShowModal(false);
    showToast({
      message: 'Estate search checkout complete.',
      severity: NotificationSeverity.SUCCESS,
    });
  };

  let bodyContent: JSX.Element | undefined = undefined;
  if (estate) {
    bodyContent = (
      <>
        {' '}
        <Typography className={classes.customizeHeader}>
          Includes all of the products below.
        </Typography>
        <PricingRow truSuffix="property" label="Address and Property History" />
        <PricingRow truSuffix="dentity" label="Name and Alias History" />
        <PricingRow truSuffix="ployment" label="Current and Former Employees" />
        <PricingRow truSuffix="liabilities" label="Current Creditors, Open Credit Cards" />
      </>
    );
  }

  return (
    <>
      <div className={classes.modalContentWrapper}>
        <PaymentForm
          allowPaymentQuantity={false}
          bodyContent={bodyContent}
          estateId={estate.id!}
          hasPayment={!!estate.estatePaymentIdRefForTrusuiteSearch}
          formName="estate-payment-form"
          handlePaymentComplete={handlePaymentComplete}
          title="Search for Property, Liabilities, and More"
          portalId={portalId}
        />
      </div>

      <div className={classes.modalButtonWrapper}>
        <Button
          className={classes.cancelButton}
          color="primary"
          onClick={() => {
            setShowModal(false);
          }}
        >
          Cancel
        </Button>{' '}
        <Button color="secondary" type="submit" form="estate-payment-form">
          Run Search
        </Button>
      </div>
    </>
  );
}
